import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import React, { useCallback } from 'react';

import { Box, Button, Grid, Icon } from '~/components/blocks';
import { MessageList, ScheduledMessageList } from '~/components/partials';
import { usePractitionerRole } from '~/hooks/use-practitioner-role';
import { Panel, PanelType } from '~/state/partials/message_modal/types';
import {
  patientDetailDialogMessageState,
  patientDetailDialogState,
} from '~/state/partials/patient_detail_dialog/atoms';
import { TabType } from '~/state/partials/patient_detail_dialog/types';

import { useFetchMessages } from './use-fetch-messages';

type Props = {
  guestPatientId: string | null | undefined;
  visitorType: 'DirectVisitor' | 'LineVisitor';
};

const ButtonWrapper = styled('div')(({ theme }) =>
  css({
    backgroundColor: theme.colors.background.bg,
    borderRadius: theme.radii.default,
    padding: theme.space.l,
    margin: theme.space.xs,
  }),
);

export const MessagePane = React.memo((props: Props) => {
  const { guestPatientId, visitorType } = props;
  const theme = useTheme();

  const { verifyRole } = usePractitionerRole();
  const isVerified = verifyRole('pharmacist');
  const setPatientDetailDialogMessageState = useSetAtom(patientDetailDialogMessageState);
  const setPatientDetailDialogState = useSetAtom(patientDetailDialogState);

  const { messages, scheduledMessages, loading } = useFetchMessages(
    guestPatientId ?? '',
    visitorType,
  );

  const handleClick = useCallback(
    (_panel: PanelType, messageId?: string) => {
      setPatientDetailDialogMessageState((_state) => ({
        ..._state,
        selectedMessageId: messageId || null,
      }));
      setPatientDetailDialogState((_state) => ({
        ..._state,
        isOpen: true,
        type: 'message' as TabType,
        patientId: guestPatientId ?? '',
      }));
    },
    [setPatientDetailDialogMessageState, setPatientDetailDialogState, guestPatientId],
  );

  return (
    <Box position="relative" height="100%" overflow="auto" padding={theme.space.l}>
      <Grid height="100%" gridTemplateRows="min-content min-content 1fr" gridTemplateColumns="1fr">
        {isVerified ? (
          <ButtonWrapper>
            <Button wide="fill" use="base" onClick={() => handleClick(Panel.initial)}>
              <Icon icon="comment" color="white" size="l" />
              患者にメッセージ送信
            </Button>
          </ButtonWrapper>
        ) : (
          <Box />
        )}
        <Box marginTop={theme.space.l}>
          {scheduledMessages.length > 0 && (
            <ScheduledMessageList scheduledMessages={scheduledMessages} onClick={handleClick} />
          )}
        </Box>
        <Box marginTop={theme.space.l} height="100%" overflow="auto">
          <MessageList loading={loading} messages={messages} onClick={handleClick} />
        </Box>
      </Grid>
    </Box>
  );
});

MessagePane.displayName = 'MessagePane';
